import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Card, Stack, Typography } from "@mui/material";
import { AccessGateScreen } from "./../AccessGateScreen";

export interface AccessDeniedScreenProps {
  /**
   * `fullScreen` (по умолчанию) — карточка на весь экран, `minHeight:
   * 100vh`, с кнопкой выхода (Layout, когда у сотрудника нет доступа ни к
   * одному пункту меню — заменяет собой Header/Sidebar целиком).
   * `page` — компактная карточка внутри контентной области (RequireAbility
   * на отдельной странице, Header/Sidebar остаются, есть куда уйти), без
   * кнопки выхода, стиль как у `EmptyListCard`.
   */
  variant?: "fullScreen" | "page";
}

/**
 * Гейт RequireAbility (shared/ui/RequireAbility, variant="page") и Layout
 * (когда у сотрудника нет доступа ни к одному пункту меню,
 * variant="fullScreen", по умолчанию) — полноэкранный вариант по образцу
 * NotPartnerScreen/NoOrganizationScreen. Ключи ещё не в проде до прогона
 * seed-all-texts, поэтому defaultValue на английском (см. бриф задачи 3).
 */
export const AccessDeniedScreen: FC<AccessDeniedScreenProps> = ({
  variant = "fullScreen",
}) => {
  const { t } = useTranslation();
  const title = t("auth.accessDenied.title", { defaultValue: "Access denied" });
  const text = t("auth.accessDenied.description", {
    defaultValue:
      "You do not have access to this section. Contact your organization administrator.",
  });

  if (variant === "page") {
    return (
      <Card
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
          p: { xs: 5, sm: 12.5 },
        }}
      >
        <Stack spacing={1} alignItems="center" textAlign="center">
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {text}
          </Typography>
        </Stack>
      </Card>
    );
  }

  return <AccessGateScreen title={title} text={text} />;
};
